import { useMemo } from 'react'
import { t } from 'i18next'
import { ColumnDef } from '@tanstack/react-table'
import { formatDateToStringIgnoreUTC } from '~/helpers'

interface Data {
  place_name: string
  [key: string]: string | number
}

interface Place {
  place_name: string
  impressions: number
}

interface ResponseData {
  groupby: string
  places: Place[]
}

export function useDigitalSignageSchema(response: ResponseData[]) {
  const data: Data[] = useMemo(() => {
    const placeMap: Record<string, Data> = {}

    response?.forEach(item => {
      item.places?.forEach(place => {
        if (place.place_name) {
          if (!placeMap[place.place_name]) {
            placeMap[place.place_name] = { place_name: place.place_name }
          }
          placeMap[place.place_name][
            formatDateToStringIgnoreUTC(item.groupby)
          ] = place.impressions || 0
        }
      })
    })

    // Ensure all places have zero for dates not present in the response
    response?.forEach(item => {
      Object.keys(placeMap).forEach(placeName => {
        if (!placeMap[placeName][formatDateToStringIgnoreUTC(item.groupby)]) {
          placeMap[placeName][formatDateToStringIgnoreUTC(item.groupby)] = 0
        }
      })
    })

    return Object.values(placeMap) || []
  }, [response])

  const columns = useMemo<Array<ColumnDef<Data>>>(() => {
    const dateColumns =
      response
        ?.map(item => item.groupby)
        ?.map(date => ({
          id: formatDateToStringIgnoreUTC(date),
          header: formatDateToStringIgnoreUTC(date, 'DD/MM/YYYY')
        })) || []

    return [
      {
        id: 'place_name',
        header: t('rm:daily.history.table.column.placeName')
      },
      ...dateColumns
    ]
  }, [response])

  const schema = useMemo(() => {
    return {
      place_name: {
        title: t('rm:daily.history.table.column.placeName'),
        render: (rowData: Data) => rowData.place_name
      },
      ...Object.fromEntries(
        columns
          .filter(col => col.id !== 'place_name')
          .map(col => [
            col.id,
            {
              title: col.header,
              render: (rowData: { [x: string]: any }) => rowData[col.id] || 0
            }
          ])
      )
    }
  }, [columns])

  return { data, columns, schema }
}
